import type { DataBlock, SingleBlock } from 'de-zurich-ui-library'
import type { MotorContract } from '@/types/motor/MotorContract'

import {
  buildComprehensiveInsuranceSection,
  buildInsuredVehicleSection,
  buildLiabilityInsuranceSection,
  buildPassengerAccidentInsuranceSection
} from '@/utils/motor/MotorDataBoxDataUtils'
import { isRedCooperationPartner } from '@/utilsTenant/RedCooperationUtils'

import { t } from '@/i18n'

export class MotorDetailsFactory {
  private readonly vehicleChangeEnabled: boolean
  private readonly isQuote: boolean

  constructor(isQuote: boolean) {
    this.isQuote = isQuote
    this.vehicleChangeEnabled =
      globalThis.ENV_CONFIG?.FEATURE_FLAGS?.MOTOR?.ENABLE_MOTOR_CHANGE === true &&
      !isRedCooperationPartner() &&
      !this.isQuote
  }

  createDetails(contract: MotorContract | null, databoxData: DataBlock[]): void {
    // clear previous databoxData
    if (!contract) return

    try {
      this.createInsuredVehicleBlock(contract, databoxData)
      this.createInsuranceBlocks(contract, databoxData)
    } catch (e) {
      console.error('ERROR prepareDataboxData', e)
    }

    if (contract?.accidentAndBreakdownCover) {
      databoxData.push({
        title: {
          label: t('motorDetails.accidentAndBreakdownCover.title')
        },
        data: [
          {
            label: t('motorDetails.accidentAndBreakdownCover.exists'),
            value: t(
              'motorDetails.accidentAndBreakdownCover.' +
                contract.accidentAndBreakdownCover
            )
          }
        ]
      })
    }

    // build and show extra insurance block
    const dataList = this.createLiabilityExtras(contract).concat(
      this.createComprehensiveExtras(contract)
    )

    if (dataList.length > 0) {
      const extraBlock: DataBlock = {
        title: {
          label: t('motorDetails.extras.title').toString()
        },
        data: dataList
      }
      databoxData.push(extraBlock)
    }
  }

  private createLiabilityExtras(contract: MotorContract): SingleBlock[] {
    if (!contract?.liabilityInsurance?.extras) {
      return []
    }

    return this.buildExtras(
      contract.liabilityInsurance.extras as Record<string, unknown>,
      'motorDetails.extras.liability.',
      'openModal_liabilityExtra_'
    )
  }

  private createInsuranceBlocks(contract: MotorContract, databoxData: DataBlock[]): void {
    if (!contract) return
    const motorContract = contract
    if (motorContract.liabilityInsurance) {
      databoxData.push(buildLiabilityInsuranceSection(motorContract.liabilityInsurance))
    }

    if (motorContract.comprehensiveInsurance) {
      databoxData.push(
        buildComprehensiveInsuranceSection(
          motorContract,
          motorContract.comprehensiveInsurance
        )
      )
    }

    if (motorContract.passengerAccidentInsurance) {
      databoxData.push(
        buildPassengerAccidentInsuranceSection(motorContract.passengerAccidentInsurance)
      )
    }
  }

  private createComprehensiveExtras(contract: MotorContract): SingleBlock[] {
    if (!contract?.comprehensiveInsurance?.extras) {
      return []
    }

    return this.buildExtras(
      contract.comprehensiveInsurance.extras as Record<string, unknown>,
      'motorDetails.extras.comprehensive.',
      'openModal_comprehensiveExtra_'
    )
  }

  private buildExtras(
    extras: Record<string, unknown>,
    labelPrefix: string,
    modalPrefix: string
  ): SingleBlock[] {
    const dataList: SingleBlock[] = []
    const sortedKeys = Object.keys(extras).sort((a, b) => a.localeCompare(b))

    const descriptionPrefix = 'motorDetails.extras.descriptions.'

    for (const key of sortedKeys) {
      if (!extras[key]) continue

      const descPath = descriptionPrefix + key
      const rawTranslation = t(descPath)
      const hasDescription =
        typeof rawTranslation === 'string' &&
        rawTranslation.trim() !== '' &&
        rawTranslation !== descPath

      dataList.push({
        label: t(labelPrefix + key),
        value: '',
        iconSpan: hasDescription
          ? {
              iconClass: 'icon-vorteile icon-motor-details-vorteile',
              href: modalPrefix + key,
              isRouterLink: false
            }
          : undefined
      })
    }

    return dataList
  }

  private createInsuredVehicleBlock(
    contract: MotorContract,
    databoxData: DataBlock[]
  ): void {
    if (contract?.insuredVehicle) {
      const insuredVehicleSection = buildInsuredVehicleSection(
        contract.insuredVehicle,
        this.vehicleChangeEnabled
      )
      insuredVehicleSection.data.push(
        {
          label: t('motorDetails.insuredVehicle.vehicleType'),
          value: contract.vehicleType.value,
          hide: !contract.vehicleType.value
        },
        {
          label: t('motorDetails.insuredVehicle.insuredMileage'),
          value: this.createInsuredMileageText(contract),
          hide: !contract.insuredVehicle.insuredMileage
        }
      )

      if (contract.circleOfDrivers) {
        if (contract.circleOfDrivers.circleOfDriversType) {
          insuredVehicleSection.data.push({
            label: t('motorDetails.circleOfDrivers'),
            value: t(
              'circleOfDrivers.values.' + contract.circleOfDrivers.circleOfDriversType
            ),
            hide: !contract.circleOfDrivers.circleOfDriversType
          })
        } else if (contract.circleOfDrivers.description) {
          insuredVehicleSection.data.push({
            label: t('motorDetails.circleOfDrivers'),
            value: contract.circleOfDrivers.description,
            hide: !contract.circleOfDrivers.description
          })
        }
      }

      if (contract.driversUnder23 != null) {
        insuredVehicleSection.data.push({
          label: t('motorDetails.driversUnder23'),
          value: t('driversUnder23.values.' + contract.driversUnder23),
          hide: false
        })
      }

      insuredVehicleSection.data.push({
        label: t('motorDetails.insuredVehicle.seasonalPeriod'),
        value: contract.insuredVehicle.seasonalPeriod as string,
        hide: !contract.insuredVehicle.seasonalPeriod
      })

      databoxData.push(insuredVehicleSection)
    }
  }

  private createInsuredMileageText(contract: MotorContract): string {
    if (!contract) return ''
    const mileage = Number(contract.insuredVehicle.insuredMileage)
    return mileage == 0
      ? t('motorDetails.insuredVehicle.alternateMileageText')
      : mileage.toLocaleString('de-DE') + ' km'
  }
}
